import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { OwnerNav } from "@/components/admin/OwnerNav";
import { DateFilter } from "@/components/admin/DateFilter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Search, Phone, Mail, MessageCircle, Sparkles, Trash2, User, CreditCard, Clock, Copy, X, ArrowLeft, CalendarDays, CalendarPlus, Bell, BellOff, Save, Building2, Globe, Tag, Check, DollarSign, TrendingUp, MessageSquare, ListChecks, ScanLine, Bot, MoreHorizontal, Send } from "lucide-react";
import { toast } from "sonner";
import { useLocale } from "@/i18n/useLocale";
import { useAuth } from "@/context/AuthContext";
import { BookMeetingDialog } from "@/components/profile/BookMeetingDialog";
import { LeadAIInsight } from "@/components/ai/AIPanels";
import { LeadFormDialog } from "@/components/admin/LeadFormDialog";
import { UserPlus } from "lucide-react";

const CURRENCIES = ["AED", "USD", "EUR", "GBP", "SAR"];
const money = (v, ccy) => { try { return `${ccy || ""} ${Number(v).toLocaleString()}`.trim(); } catch { return `${ccy || ""} ${v}`.trim(); } };

const STAGES = ["new", "contacted", "qualified", "meeting", "opportunity", "customer", "not_interested"];

const STAGE_BADGE = {
  new: "text-[#D6A653] border-[#D6A653]/40 bg-[#D6A653]/10",
  contacted: "text-sky-300 border-sky-400/40 bg-sky-400/10",
  qualified: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  meeting: "text-violet-300 border-violet-400/40 bg-violet-400/10",
  opportunity: "text-fuchsia-300 border-fuchsia-400/40 bg-fuchsia-400/10",
  customer: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  not_interested: "text-white/50 border-white/20 bg-white/5",
};
const LEGACY_STAGE = { meeting_booked: "meeting", converted: "customer", archived: "not_interested", won: "customer", lost: "not_interested", follow_up: "contacted" };
const MSTATUS_KEY = { requested: "requested", time_proposed: "time_proposed", scheduled: "confirmed", confirmed: "confirmed", rescheduled: "confirmed", completed: "completed", cancelled: "cancelled", declined: "declined", "no-show": "noshow" };


const norm = (s) => { const v = (s || "new").toLowerCase(); return LEGACY_STAGE[v] || v; };
const TEMP_BADGE = { hot: "text-red-300 border-red-400/40 bg-red-400/10", warm: "text-amber-300 border-amber-400/40 bg-amber-400/10", cold: "text-sky-300 border-sky-400/40 bg-sky-400/10" };
const TEMP_ICON = { hot: "🔥", warm: "", cold: "" };
const effTemp = (l) => { const o = l.lead_temperature_override; return (o === "hot" || o === "warm" || o === "cold") ? o : (l.lead_temperature || "cold"); };
const digits = (p) => (p || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
const fmt = (iso, tz) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz || undefined });

// Phase 1 — relative-time formatter used by Last-contacted pill and timeline feed.
function relTime(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Leads() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { user, memberships } = useAuth();
  const canEditFin = (l) => {
    if (!user) return false;
    if (user.role === "SUPER_ADMIN") return true;
    const wid = l?.workspace_id;
    return (memberships || []).some((m) => (!wid || m.workspace_id === wid) && ["WORKSPACE_OWNER", "WORKSPACE_ADMIN"].includes(m.role));
  };
  const sLabel = (st) => t(`leads.stage_${st}`, { defaultValue: st });
  const mLabel = (ms) => t(`leads.mstatus_${MSTATUS_KEY[ms] || "requested"}`, { defaultValue: ms });
  const evLabel = (e) => t(`leads.ev_${e}`, { defaultValue: e });
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [mByLead, setMByLead] = useState({});
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [card, setCard] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [capturedBy, setCapturedBy] = useState("all");
  const [temp, setTemp] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [dateRange, setDateRange] = useState({ preset: "all", start: null, end: null });
  const [openLead, setOpenLead] = useState(null);
  const [drawerTab, setDrawerTab] = useState("timeline"); // Phase 1 — Timeline default
  const [draft, setDraft] = useState("");
  const [gen, setGen] = useState(false);
  const [aiOpts, setAiOpts] = useState({ tone: "professional", channel: "email", language: "en" });
  const [bookLead, setBookLead] = useState(null);
  const [edit, setEdit] = useState(null); // {company,title,website,notes,tags,met_at,event}
  const [savingEdit, setSavingEdit] = useState(false);
  const [remindWhen, setRemindWhen] = useState("");
  const [savingRemind, setSavingRemind] = useState(false);
  const [fin, setFin] = useState(null);
  const [savingFin, setSavingFin] = useState(false);
  const [hsConn, setHsConn] = useState(null);
  const [syncingHs, setSyncingHs] = useState(false);

  const load = () => {
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
    api.get("/admin/cards").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(data || [])).catch(() => {});
    api.get("/admin/team-members").then(({ data }) => setMembers(data || [])).catch(() => {});
    api.get("/integrations/hubspot/status").then(({ data }) => setHsConn(data)).catch(() => setHsConn(null));
    Promise.all(["upcoming", "past", "cancelled"].map((f) => api.get("/admin/meetings", { params: { filter: f } }).then((r) => r.data).catch(() => [])))
      .then((lists) => {
        const map = {};
        lists.flat().forEach((m) => { if (m.lead_id) map[m.lead_id] = m; });
        setMByLead(map);
      });
  };
  useEffect(() => { load(); }, []);

  const stageOf = (l) => {
    const s = norm(l.status);
    if (s !== "new") return s;
    if (mByLead[l.id] || l.source === "meeting_booking") return "meeting";
    return "new";
  };

  // open lead from ?lead=
  useEffect(() => {
    const id = params.get("lead");
    if (id && leads) { const l = leads.find((x) => x.id === id); if (l) openDetail(l); }
  }, [leads]); // eslint-disable-line

  const counts = useMemo(() => {
    const c = { all: 0 }; STAGES.forEach((s) => (c[s] = 0));
    (leads || []).forEach((l) => { c.all++; c[stageOf(l)]++; });
    return c;
  }, [leads, mByLead]);

  const filtered = useMemo(() => {
    let list = leads || [];
    if (tab !== "all") list = list.filter((l) => stageOf(l) === tab);
    if (source !== "all") list = list.filter((l) => (l.source || "inquiry") === source);
    if (card !== "all") list = list.filter((l) => l.cardSlug === card);
    if (eventFilter !== "all") list = list.filter((l) => (eventFilter === "__none" ? !l.event_id && !l.event : l.event_id === eventFilter));
    if (capturedBy !== "all") list = list.filter((l) => (l.captured_by || "") === capturedBy);
    if (temp !== "all") list = list.filter((l) => effTemp(l) === temp);
    if (dateRange?.start) {
      const s = new Date(dateRange.start).getTime();
      const e = new Date(dateRange.end).getTime();
      list = list.filter((l) => { const ts = new Date(l.created_at).getTime(); return ts >= s && ts <= e; });
    }
    if (q.trim()) { const s = q.toLowerCase(); list = list.filter((l) => [l.name, l.email, l.phone].some((v) => (v || "").toLowerCase().includes(s))); }
    const arr = [...list];
    if (sortBy === "score_desc") arr.sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
    else if (sortBy === "score_asc") arr.sort((a, b) => (a.lead_score || 0) - (b.lead_score || 0));
    else if (sortBy === "followup") arr.sort((a, b) => (a.next_follow_up || "9999").localeCompare(b.next_follow_up || "9999"));
    else arr.sort((a, b) => new Date(b.last_activity || b.created_at) - new Date(a.last_activity || a.created_at));
    return arr;
  }, [leads, tab, source, card, eventFilter, capturedBy, temp, sortBy, dateRange, q, mByLead]);

  const openDetail = async (l) => {
    setOpenLead(l); setDraft("");
    setEdit({ company: l.company || "", title: l.title || "", website: l.website || "", notes: l.notes || "", tags: (l.tags || []).join(", "), met_at: l.met_at || "", event: l.event || "" });
    const att = l.revenue_attribution || {};
    setFin({
      opportunity_value: l.opportunity_value ?? "",
      opportunity_currency: l.opportunity_currency || "AED",
      expected_close_date: l.expected_close_date ? String(l.expected_close_date).slice(0, 10) : "",
      actual_revenue: l.actual_revenue ?? "",
      actual_revenue_currency: l.actual_revenue_currency || l.opportunity_currency || "AED",
      revenue_recorded_at: l.revenue_recorded_at ? String(l.revenue_recorded_at).slice(0, 10) : "",
      attribution: att.type === "event" && att.event_id ? att.event_id : (att.type === "other" ? "__other" : "__organic"),
    });
    setRemindWhen(l.next_follow_up ? String(l.next_follow_up).slice(0, 16) : "");
    if (!l.read) { try { await api.patch(`/admin/leads/${l.id}`); setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, read: true } : x)); } catch (_) {} }
  };
  const closeDetail = () => { setOpenLead(null); if (params.get("lead")) { params.delete("lead"); setParams(params, { replace: true }); } };

  const openAI = async (l) => {
    await openDetail(l);
    setTimeout(() => document.getElementById("lead-ai-section")?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
  };

  const changeStage = async (l, st) => {
    try { await api.patch(`/admin/leads/${l.id}/status`, { status: st }); toast.success(t("leads.moved", { stage: sLabel(st) })); setLeads((ls) => ls.map((x) => x.id === l.id ? { ...x, status: st } : x)); setOpenLead((o) => o && o.id === l.id ? { ...o, status: st } : o); }
    catch { toast.error(t("leads.couldNotUpdate")); }
  };
  const remove = async (l) => { if (!window.confirm(t("leads.deleteConfirm"))) return; try { await api.delete(`/admin/leads/${l.id}`); toast.success(t("leads.deleted")); closeDetail(); setLeads((ls) => ls.filter((x) => x.id !== l.id)); } catch { toast.error(t("leads.deleteFailed")); } };

  const genAI = async (l) => {
    setGen(true); setDraft("");
    try { const { data } = await api.post("/ai/followup", { lead_name: l.name, company: l.company || "", notes: l.notes || l.message || l.interest || "", owner_name: "", tone: aiOpts.tone, channel: aiOpts.channel, language: aiOpts.language }); setDraft(data.draft); toast.success(t("leads.draftReady", { provider: data.provider })); }
    catch { toast.error(t("leads.couldNotDraft")); } finally { setGen(false); }
  };

  const patchLeadLocal = (id, patch) => {
    setLeads((ls) => ls.map((x) => x.id === id ? { ...x, ...patch } : x));
    setOpenLead((o) => o && o.id === id ? { ...o, ...patch } : o);
  };

  const syncHubspot = async (l) => {
    setSyncingHs(true);
    try {
      const { data } = await api.post(`/admin/leads/${l.id}/sync-hubspot`);
      patchLeadLocal(l.id, { crm_sync: data.crm_sync });
      toast.success(t("hubspot.synced"));
    } catch (e) {
      const cs = e?.response?.data?.detail?.crm_sync;
      if (cs) patchLeadLocal(l.id, { crm_sync: cs });
      toast.error(e?.response?.data?.detail?.detail || t("hubspot.syncFailed"));
    } finally { setSyncingHs(false); }
  };

  const saveFinancials = async (l) => {
    setSavingFin(true);
    const payload = {
      opportunity_value: fin.opportunity_value === "" ? null : Number(fin.opportunity_value),
      opportunity_currency: fin.opportunity_currency,
      expected_close_date: fin.expected_close_date || "",
    };
    if (fin.actual_revenue === "" || fin.actual_revenue === null) {
      payload.actual_revenue = null;
    } else {
      payload.actual_revenue = Number(fin.actual_revenue);
      payload.actual_revenue_currency = fin.actual_revenue_currency;
      if (fin.revenue_recorded_at) payload.revenue_recorded_at = new Date(fin.revenue_recorded_at + "T00:00:00").toISOString();
      if (fin.attribution === "__organic") { payload.revenue_attribution_type = "organic"; payload.revenue_attribution_event_id = null; }
      else if (fin.attribution === "__other") { payload.revenue_attribution_type = "other"; payload.revenue_attribution_event_id = null; }
      else { payload.revenue_attribution_type = "event"; payload.revenue_attribution_event_id = fin.attribution; }
    }
    try {
      const { data } = await api.patch(`/admin/leads/${l.id}/financials`, payload);
      patchLeadLocal(l.id, {
        opportunity_value: data.opportunity_value, opportunity_currency: data.opportunity_currency,
        expected_close_date: data.expected_close_date, actual_revenue: data.actual_revenue,
        actual_revenue_currency: data.actual_revenue_currency, revenue_recorded_at: data.revenue_recorded_at,
        revenue_attribution: data.revenue_attribution, timeline: data.timeline,
      });
      toast.success(t("leads.financialsSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("leads.couldNotUpdate"));
    } finally { setSavingFin(false); }
  };

  const saveFields = async (l) => {
    setSavingEdit(true);
    const tags = edit.tags.split(",").map((s) => s.trim()).filter(Boolean);
    const payload = { company: edit.company, title: edit.title, website: edit.website, notes: edit.notes, tags, met_at: edit.met_at, event: edit.event };
    try { await api.patch(`/admin/leads/${l.id}/fields`, payload); patchLeadLocal(l.id, payload); toast.success(t("leads.detailsSaved")); }
    catch { toast.error(t("leads.couldNotUpdate")); } finally { setSavingEdit(false); }
  };

  const saveReminder = async (l) => {
    if (!remindWhen) { toast.error(t("leads.pickReminder")); return; }
    setSavingRemind(true);
    const iso = new Date(remindWhen).toISOString();
    try { await api.post(`/admin/leads/${l.id}/remind`, { when: iso }); patchLeadLocal(l.id, { next_follow_up: iso }); toast.success(t("leads.reminderSet")); }
    catch { toast.error(t("leads.couldNotUpdate")); } finally { setSavingRemind(false); }
  };

  const clearReminder = async (l) => {
    setSavingRemind(true);
    try { await api.delete(`/admin/leads/${l.id}/remind`); setRemindWhen(""); patchLeadLocal(l.id, { next_follow_up: "" }); toast.success(t("leads.reminderCleared")); }
    catch { toast.error(t("leads.couldNotUpdate")); } finally { setSavingRemind(false); }
  };

  const completeFollowUp = async (l) => {
    setSavingRemind(true);
    try { const { data } = await api.post(`/admin/leads/${l.id}/complete-follow-up`); patchLeadLocal(l.id, { next_follow_up: "", follow_up_completed_at: data.follow_up_completed_at }); toast.success(t("leads.followUpDone")); }
    catch { toast.error(t("leads.couldNotUpdate")); } finally { setSavingRemind(false); }
  };

  const setTemperature = async (l, temperature) => {
    try {
      const { data } = await api.post(`/admin/leads/${l.id}/temperature`, { temperature });
      patchLeadLocal(l.id, { lead_temperature_override: data.lead_temperature_override, lead_score: data.lead_score, lead_temperature: data.lead_temperature });
      toast.success(t("leads.qualityUpdated"));
    } catch { toast.error(t("leads.couldNotUpdate")); }
  };

  const multiCard = cards.length > 1;
  const QA = ({ icon: Icon, href, onClick, label, testId }) => {
    const cls = "flex h-9 w-9 items-center justify-center rounded-lg border border-white/12 bg-white/[0.02] text-white/70 transition-colors hover:border-[#D6A653]/50 hover:text-white";
    return href ? <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className={cls} title={label} data-testid={testId}><Icon className="h-4 w-4" /></a>
      : <button onClick={(e) => { e.stopPropagation(); onClick(); }} className={cls} title={label} data-testid={testId}><Icon className="h-4 w-4" /></button>;
  };

  return (
    <div className="aria-dark relative min-h-screen bg-[#050607] text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="leads-page">
      <div className="grain-overlay" style={{ opacity: 0.04 }} />
      <OwnerNav active="leads" />

      <main className="relative mx-auto max-w-5xl px-4 py-8 sm:px-8">
        <div className="mb-4 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-white/50 hover:text-white" data-testid="leads-back"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex-1">
            <h2 className="text-2xl font-light tracking-tight text-white">{t("leads.title")}</h2>
            <p className="text-sm text-white/45">{t("leads.subtitle")}</p>
          </div>
          <button onClick={() => setAddLeadOpen(true)} data-testid="leads-add-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-[#D6A653] px-3.5 py-2 text-sm font-medium text-black hover:brightness-110">
            <UserPlus className="h-4 w-4" /> Add lead
          </button>
        </div>

        {/* pipeline tabs */}
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="leads-pipeline">
          {["all", ...STAGES].map((s) => (
            <button key={s} onClick={() => setTab(s)} data-testid={`pipeline-${s}`}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-all ${tab === s ? "border-[#D6A653] bg-[#D6A653]/12 text-white" : "border-white/10 text-white/55 hover:border-white/25"}`}>
              {s === "all" ? t("leads.all") : sLabel(s)}
              <span className="rounded-full bg-white/10 px-1.5 text-[10px] text-white/70">{counts[s] || 0}</span>
            </button>
          ))}
        </div>

        {/* filters */}
        <div className="mb-5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <div className="relative col-span-2 sm:w-56">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("leads.search")} className="h-9 w-full rounded-lg border border-white/12 bg-[#0A0B0D] pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-[#D6A653]/50 focus:outline-none" data-testid="leads-search" />
          </div>
          <Select value={source} onValueChange={setSource}><SelectTrigger className="h-9 text-xs" data-testid="leads-filter-source"><SelectValue placeholder={t("leads.source")} /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="all">{t("leads.allSources")}</SelectItem><SelectItem value="inquiry">{t("leads.inquiry")}</SelectItem><SelectItem value="meeting_booking">{t("leads.meetingBooking")}</SelectItem><SelectItem value="event_badge_scan">{t("leads.sourceEventBadge")}</SelectItem><SelectItem value="business_card_scan">{t("leads.sourceBusinessCard")}</SelectItem><SelectItem value="badge_scan">{t("leads.sourceBadge")}</SelectItem><SelectItem value="qr_scan">{t("leads.sourceQr")}</SelectItem></SelectContent></Select>
          <Select value={eventFilter} onValueChange={setEventFilter}><SelectTrigger className="h-9 text-xs" data-testid="leads-filter-event"><SelectValue placeholder={t("leads.eventFilter")} /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="all">{t("leads.allEvents")}</SelectItem><SelectItem value="__none">{t("leads.noEvent")}</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>
          {members.length > 1 ? <Select value={capturedBy} onValueChange={setCapturedBy}><SelectTrigger className="h-9 text-xs" data-testid="leads-filter-capturedby"><SelectValue placeholder={t("leads.capturedBy")} /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="all">{t("leads.allMembers")}</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select> : null}
          {multiCard ? <Select value={card} onValueChange={setCard}><SelectTrigger className="h-9 text-xs" data-testid="leads-filter-card"><SelectValue placeholder={t("leads.card")} /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="all">{t("leads.allCards")}</SelectItem>{cards.map((c) => <SelectItem key={c.slug} value={c.slug}>/{c.slug}</SelectItem>)}</SelectContent></Select> : null}
          <div className="col-span-2 sm:col-span-1" data-testid="leads-filter-date"><DateFilter value={dateRange} onChange={setDateRange} testId="leads-date-filter" allowAll /></div>
          <Select value={temp} onValueChange={setTemp}><SelectTrigger className="h-9 text-xs" data-testid="leads-filter-temp"><SelectValue placeholder={t("leads.quality")} /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="all">{t("leads.allQuality")}</SelectItem><SelectItem value="hot">🔥 {t("leads.hot")}</SelectItem><SelectItem value="warm">{t("leads.warm")}</SelectItem><SelectItem value="cold">{t("leads.cold")}</SelectItem></SelectContent></Select>
          <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="h-9 text-xs" data-testid="leads-sort"><SelectValue /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="recent">{t("leads.sortRecent")}</SelectItem><SelectItem value="score_desc">{t("leads.sortScoreDesc")}</SelectItem><SelectItem value="score_asc">{t("leads.sortScoreAsc")}</SelectItem><SelectItem value="followup">{t("leads.sortFollowup")}</SelectItem></SelectContent></Select>
          {(source !== "all" || card !== "all" || eventFilter !== "all" || capturedBy !== "all" || temp !== "all" || q.trim() || dateRange?.start) && (
            <button
              onClick={() => { setQ(""); setSource("all"); setCard("all"); setEventFilter("all"); setCapturedBy("all"); setTemp("all"); setSortBy("recent"); setDateRange({ preset: "all", start: null, end: null }); }}
              data-testid="leads-clear-filters"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-white/12 px-3 text-xs text-white/70 hover:border-red-400/40 hover:text-red-300"
            >
              <X className="h-3 w-3" /> {t("leads.clearFilters", "Clear filters")}
            </button>
          )}
        </div>

        {leads === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#D6A653]" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/12 py-20 text-center text-white/50" data-testid="leads-empty">{t("leads.empty")}</div>
        ) : (
          <div className="space-y-2.5">
            {filtered.map((l) => {
              const st = stageOf(l); const m = mByLead[l.id];
              const lastEv = (l.timeline || [])[l.timeline?.length - 1];
              return (
                <div key={l.id} onClick={() => openDetail(l)} data-testid={`lead-row-${l.id}`}
                  className={`cursor-pointer rounded-2xl border p-4 transition-colors ${l.read ? "border-white/10 bg-[#0A0B0D]" : "border-[#D6A653]/30 bg-[#D6A653]/[0.05]"} hover:border-white/25`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-white">{l.name}</span>
                        {!l.read ? <span className="rounded-full bg-[#D6A653] px-1.5 py-0.5 text-[9px] font-semibold text-[#050607]">{t("leads.new")}</span> : null}
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${STAGE_BADGE[st]}`} data-testid={`lead-stage-${l.id}`}>{sLabel(st)}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${TEMP_BADGE[effTemp(l)]}`} data-testid={`lead-temp-${l.id}`}>{TEMP_ICON[effTemp(l)]}{t(`leads.${effTemp(l)}`)}{typeof l.lead_score === "number" ? ` ${l.lead_score}` : ""}{l.lead_temperature_override ? " ·M" : ""}</span>
                        {m ? <span className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] text-white/60">{mLabel(m.status)}</span> : null}
                        {l.opportunity_value != null ? <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] text-emerald-300" data-testid={`lead-value-${l.id}`}>{money(l.opportunity_value, l.opportunity_currency)}</span> : null}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-white/50">
                        {l.company || l.title ? <span className="flex items-center gap-1 text-white/60"><Building2 className="h-3 w-3" />{[l.title, l.company].filter(Boolean).join(" · ")}</span> : null}
                        {l.email ? <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</span> : null}
                        {l.phone ? <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</span> : null}
                        <span className="text-white/35">/{l.cardSlug}</span>
                        <span className="text-white/30">· {(l.source || "inquiry").replace(/_/g, " ")}</span>
                      </div>
                      {(l.tags || []).length ? (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`lead-tags-${l.id}`}>
                          {(l.tags || []).slice(0, 4).map((tg) => <span key={tg} className="rounded-full border border-white/12 bg-white/5 px-1.5 py-0.5 text-[9px] text-white/55">{tg}</span>)}
                        </div>
                      ) : null}
                      <p className="mt-1 text-xs text-white/40">{lastEv ? evLabel(lastEv.event) : t("leads.inquiry")} · {fmt(l.last_activity || l.created_at)}</p>
                      {l.last_contacted_at ? (
                        <p className="mt-0.5 text-[10px] text-white/45"><Clock className="mr-1 inline h-2.5 w-2.5" />Last contacted: {relTime(l.last_contacted_at)}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                      {l.phone ? <QA icon={Phone} href={`tel:${l.phone}`} label={t("leads.call")} testId={`lead-call-${l.id}`} /> : null}
                      {l.phone ? <QA icon={MessageCircle} href={`https://wa.me/${digits(l.phone)}`} label={t("leads.whatsapp")} testId={`lead-wa-${l.id}`} /> : null}
                      {l.email ? <QA icon={Mail} href={`mailto:${l.email}`} label={t("leads.email")} testId={`lead-email-${l.id}`} /> : null}
                      <button onClick={(e) => { e.stopPropagation(); openAI(l); }} title={t("leads.aiFollowup")} data-testid={`lead-ai-${l.id}`} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-2.5 text-xs font-medium text-[#D6A653] transition-colors hover:bg-[#D6A653]/20">
                        <Sparkles className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t("leads.aiFollowup")}</span><span className="sm:hidden">{t("leads.ai")}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* lead detail */}
      <Dialog open={!!openLead} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="aria-dark max-h-[88vh] max-w-lg overflow-y-auto border-white/10 bg-[#0A0B0D] text-white" data-testid="lead-drawer" data-testid-alt="lead-detail">
          {openLead ? (() => {
            const l = openLead; const st = stageOf(l); const m = mByLead[l.id];
            const timeline = [{ at: l.created_at, event: "created" }, ...(l.timeline || [])];
            const lc = relTime(l.last_contacted_at);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex flex-wrap items-center gap-2 text-white">
                    <User className="h-5 w-5 text-[#D6A653]" /> {l.name}
                    <span
                      data-testid="lead-last-contacted-pill"
                      className={`ml-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-normal ${lc ? "border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-200" : "border-white/12 bg-white/[0.03] text-white/50"}`}
                    >
                      <Clock className="h-3 w-3" /> {lc ? `Last contacted: ${lc}` : "Never contacted"}
                    </span>
                  </DialogTitle>
                </DialogHeader>

                {/* Phase 1 — tabs */}
                <div className="mt-2 flex gap-1 border-b border-white/10">
                  <button
                    onClick={() => setDrawerTab("timeline")}
                    data-testid="lead-drawer-tab-timeline"
                    className={`-mb-px border-b-2 px-3 py-1.5 text-xs ${drawerTab === "timeline" ? "border-[#D6A653] text-white" : "border-transparent text-white/50 hover:text-white/80"}`}
                  >Timeline</button>
                  <button
                    onClick={() => setDrawerTab("details")}
                    data-testid="lead-drawer-tab-details"
                    className={`-mb-px border-b-2 px-3 py-1.5 text-xs ${drawerTab === "details" ? "border-[#D6A653] text-white" : "border-transparent text-white/50 hover:text-white/80"}`}
                  >Details</button>
                </div>

                <div className="space-y-4 pt-3 text-sm">
                  <div className="flex flex-wrap gap-2">
                    {l.phone ? <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs hover:border-[#D6A653]/50" data-testid="detail-call"><Phone className="h-3.5 w-3.5 text-[#D6A653]" /> {t("leads.call")}</a> : null}
                    {l.phone ? <a href={`https://wa.me/${digits(l.phone)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs hover:border-[#D6A653]/50" data-testid="detail-wa"><MessageCircle className="h-3.5 w-3.5 text-[#D6A653]" /> {t("leads.whatsapp")}</a> : null}
                    {l.email ? <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs hover:border-[#D6A653]/50" data-testid="detail-email"><Mail className="h-3.5 w-3.5 text-[#D6A653]" /> {t("leads.email")}</a> : null}
                    <button onClick={() => { setOpenLead(null); setBookLead(l); }} className="flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs hover:border-[#D6A653]/50" data-testid="detail-book"><CalendarPlus className="h-3.5 w-3.5 text-[#D6A653]" /> {t("leads.bookMeeting")}</button>
                    <button onClick={() => { setDrawerTab("details"); setTimeout(() => document.getElementById("lead-remind-section")?.scrollIntoView({ behavior: "smooth", block: "center" }), 60); }} className="flex items-center gap-1.5 rounded-lg border border-white/12 px-3 py-1.5 text-xs hover:border-[#D6A653]/50" data-testid="detail-remind"><Bell className="h-3.5 w-3.5 text-[#D6A653]" /> {t("leads.remindMe")}</button>
                  </div>

                  {drawerTab === "timeline" ? (
                    <LeadTimeline leadId={l.id} onNoteAdded={() => { load(); }} user={user} />
                  ) : (
                  <>

                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="grid grid-cols-2 gap-y-2 text-xs">
                      {l.email ? <><span className="text-white/45">{t("leads.emailLabel")}</span><span className="text-right text-white/85">{l.email}</span></> : null}
                      {l.phone ? <><span className="text-white/45">{t("leads.phone")}</span><span className="text-right text-white/85">{l.phone}</span></> : null}
                      <span className="text-white/45">{t("leads.source")}</span><span className="text-right text-white/85">{(l.source || "inquiry").replace("_", " ")}</span>
                      <span className="flex items-center gap-1 text-white/45"><CreditCard className="h-3 w-3" /> {t("leads.card")}</span><span className="text-right text-[#D6A653]">/{l.cardSlug}</span>
                    </div>
                    {l.message ? <p className="mt-3 border-t border-white/8 pt-3 text-white/75">“{l.message}”</p> : null}
                  </div>

                  {/* AI Lead Insight (on-demand, cached, metered) */}
                  <LeadAIInsight leadId={l.id} />

                  {/* editable contact context */}
                  {edit ? (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-fields">
                    <p className="mb-3 text-xs uppercase tracking-wider text-[#D6A653]">{t("leads.contactDetails")}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="col-span-1 text-[11px] text-white/45">{t("leads.company")}
                        <input value={edit.company} onChange={(e) => setEdit((o) => ({ ...o, company: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-company" />
                      </label>
                      <label className="col-span-1 text-[11px] text-white/45">{t("leads.jobTitle")}
                        <input value={edit.title} onChange={(e) => setEdit((o) => ({ ...o, title: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-title" />
                      </label>
                      <label className="col-span-2 text-[11px] text-white/45">{t("leads.website")}
                        <input value={edit.website} onChange={(e) => setEdit((o) => ({ ...o, website: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-website" />
                      </label>
                      <label className="col-span-1 text-[11px] text-white/45">{t("leads.event")}
                        <input value={edit.event} onChange={(e) => setEdit((o) => ({ ...o, event: e.target.value }))} placeholder={t("leads.eventPlaceholder")} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white placeholder:text-white/25 focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-event" />
                      </label>
                      <label className="col-span-1 text-[11px] text-white/45">{t("leads.metAt")}
                        <input type="date" value={edit.met_at ? String(edit.met_at).slice(0, 10) : ""} onChange={(e) => setEdit((o) => ({ ...o, met_at: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-metat" />
                      </label>
                      <label className="col-span-2 flex items-center gap-1 text-[11px] text-white/45"><Tag className="h-3 w-3" /> {t("leads.tags")}
                        <input value={edit.tags} onChange={(e) => setEdit((o) => ({ ...o, tags: e.target.value }))} placeholder={t("leads.tagsPlaceholder")} className="ml-2 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white placeholder:text-white/25 focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-tags" />
                      </label>
                      <label className="col-span-2 text-[11px] text-white/45">{t("leads.notes")}
                        <textarea rows={2} value={edit.notes} onChange={(e) => setEdit((o) => ({ ...o, notes: e.target.value }))} className="mt-1 w-full rounded-lg border border-white/12 bg-[#0A0B0D] p-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="field-notes" />
                      </label>
                    </div>
                    <button onClick={() => saveFields(l)} disabled={savingEdit} className="mt-3 flex items-center gap-1.5 rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs font-medium text-[#D6A653] hover:bg-[#D6A653]/20 disabled:opacity-60" data-testid="detail-save-fields">
                      {savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t("leads.saveDetails")}
                    </button>
                  </div>
                  ) : null}

                  {/* Deal & Revenue (financials) */}
                  {fin ? (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-financials">
                    <p className="mb-3 flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#D6A653]"><DollarSign className="h-3.5 w-3.5" /> {t("leads.financials")}</p>
                    {canEditFin(l) ? (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="col-span-1 text-[11px] text-white/45">{t("leads.opportunityValue")}
                            <input type="number" min="0" step="any" value={fin.opportunity_value} onChange={(e) => setFin((o) => ({ ...o, opportunity_value: e.target.value }))} placeholder="—" className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white placeholder:text-white/25 focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-opportunity-value" />
                          </label>
                          <label className="col-span-1 text-[11px] text-white/45">{t("leads.currency")}
                            <select value={fin.opportunity_currency} onChange={(e) => setFin((o) => ({ ...o, opportunity_currency: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-opportunity-currency">
                              {CURRENCIES.map((c) => <option key={c} value={c} className="bg-[#0A0B0D]">{c}</option>)}
                            </select>
                          </label>
                          <label className="col-span-2 text-[11px] text-white/45">{t("leads.expectedClose")}
                            <input type="date" value={fin.expected_close_date} onChange={(e) => setFin((o) => ({ ...o, expected_close_date: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-expected-close" />
                          </label>
                        </div>
                        <div className="mt-3 border-t border-white/8 pt-3">
                          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/45"><TrendingUp className="h-3 w-3" /> {t("leads.recordRevenue")}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <label className="col-span-1 text-[11px] text-white/45">{t("leads.actualRevenue")}
                              <input type="number" min="0" step="any" value={fin.actual_revenue} onChange={(e) => setFin((o) => ({ ...o, actual_revenue: e.target.value }))} placeholder="—" className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white placeholder:text-white/25 focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-revenue-value" />
                            </label>
                            <label className="col-span-1 text-[11px] text-white/45">{t("leads.currency")}
                              <select value={fin.actual_revenue_currency} onChange={(e) => setFin((o) => ({ ...o, actual_revenue_currency: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-revenue-currency">
                                {CURRENCIES.map((c) => <option key={c} value={c} className="bg-[#0A0B0D]">{c}</option>)}
                              </select>
                            </label>
                            <label className="col-span-1 text-[11px] text-white/45">{t("leads.revenueDate")}
                              <input type="date" value={fin.revenue_recorded_at} onChange={(e) => setFin((o) => ({ ...o, revenue_recorded_at: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-revenue-date" />
                            </label>
                            <label className="col-span-1 text-[11px] text-white/45">{t("leads.attributeTo")}
                              <select value={fin.attribution} onChange={(e) => setFin((o) => ({ ...o, attribution: e.target.value }))} className="mt-1 h-8 w-full rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="fin-attribution">
                                <option value="__organic" className="bg-[#0A0B0D]">{t("leads.noEventOrganic")}</option>
                                <option value="__other" className="bg-[#0A0B0D]">{t("leads.other")}</option>
                                {events.map((e) => <option key={e.id} value={e.id} className="bg-[#0A0B0D]">{e.name}</option>)}
                              </select>
                            </label>
                          </div>
                        </div>
                        <button onClick={() => saveFinancials(l)} disabled={savingFin} className="mt-3 flex items-center gap-1.5 rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs font-medium text-[#D6A653] hover:bg-[#D6A653]/20 disabled:opacity-60" data-testid="fin-save">
                          {savingFin ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t("leads.saveFinancials")}
                        </button>
                      </>
                    ) : (
                      <div className="grid grid-cols-2 gap-y-1 text-xs" data-testid="detail-financials-readonly">
                        <span className="text-white/45">{t("leads.opportunityValue")}</span>
                        <span className="text-right text-white/85">{l.opportunity_value != null ? money(l.opportunity_value, l.opportunity_currency) : t("leads.finNotSet")}</span>
                        <span className="text-white/45">{t("leads.actualRevenue")}</span>
                        <span className="text-right text-white/85">{l.actual_revenue != null ? money(l.actual_revenue, l.actual_revenue_currency) : t("leads.finNotSet")}</span>
                        <p className="col-span-2 mt-2 text-[11px] text-white/35">{t("leads.finAdminOnly")}</p>
                      </div>
                    )}
                  </div>
                  ) : null}

                  {/* HubSpot CRM sync */}
                  {hsConn?.connected ? (() => {
                    const cs = l.crm_sync || {}; const st = cs.status || "not_synced";
                    const badge = { synced: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300", pending: "border-amber-400/30 bg-amber-400/10 text-amber-300", failed: "border-red-400/30 bg-red-400/10 text-red-300", not_synced: "border-white/15 bg-white/5 text-white/50" }[st];
                    return (
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-crm">
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-wider text-[#D6A653]">HubSpot</p>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${badge}`} data-testid="crm-status">{t(`hubspot.state_${st}`)}</span>
                      </div>
                      {cs.last_synced_at ? <p className="mt-1 text-[11px] text-white/40" data-testid="crm-last-synced">{t("hubspot.lastSynced", { when: fmt(cs.last_synced_at) })}</p> : null}
                      {st === "failed" && cs.last_error ? <p className="mt-1 text-[11px] text-red-300/70" data-testid="crm-error">{cs.last_error}</p> : null}
                      <button onClick={() => syncHubspot(l)} disabled={syncingHs} className="mt-2 flex items-center gap-1.5 rounded-lg border border-[#D6A653]/50 bg-[#D6A653]/10 px-3 py-1.5 text-xs font-medium text-[#D6A653] hover:bg-[#D6A653]/20 disabled:opacity-60" data-testid="crm-sync-btn">
                        {syncingHs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />} {st === "failed" ? t("hubspot.retrySync") : t("hubspot.syncToHubspot")}
                      </button>
                    </div>
                    );
                  })() : null}

                  {/* follow-up reminder */}
                  <div id="lead-remind-section" className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-reminder">
                    <p className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#D6A653]"><Bell className="h-3.5 w-3.5" /> {t("leads.followUpReminder")}</p>
                    {l.next_follow_up ? <p className="mb-2 text-xs text-white/60" data-testid="reminder-current">{t("leads.reminderOn", { when: fmt(l.next_follow_up) })}</p> : null}
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="datetime-local" value={remindWhen} onChange={(e) => setRemindWhen(e.target.value)} className="h-8 flex-1 rounded-lg border border-white/12 bg-[#0A0B0D] px-2 text-xs text-white focus:border-[#D6A653]/50 focus:outline-none" data-testid="reminder-input" />
                      <button onClick={() => saveReminder(l)} disabled={savingRemind} className="flex items-center gap-1 rounded-lg bg-[#D6A653] px-3 py-1.5 text-xs font-medium text-[#050607] hover:bg-[#E8B764] disabled:opacity-60" data-testid="reminder-set">{savingRemind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} {t("leads.setReminder")}</button>
                      {l.next_follow_up ? <button onClick={() => clearReminder(l)} disabled={savingRemind} className="flex items-center gap-1 rounded-lg border border-white/12 px-3 py-1.5 text-xs text-white/60 hover:text-white disabled:opacity-60" data-testid="reminder-clear"><BellOff className="h-3.5 w-3.5" /> {t("leads.clearReminder")}</button> : null}
                      {l.next_follow_up && !l.follow_up_completed_at ? <button onClick={() => completeFollowUp(l)} disabled={savingRemind} className="flex items-center gap-1 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-60" data-testid="reminder-complete"><Check className="h-3.5 w-3.5" /> {t("leads.markFollowUpDone")}</button> : null}
                      {l.follow_up_completed_at ? <span className="flex items-center gap-1 text-xs text-emerald-300" data-testid="reminder-done"><Check className="h-3.5 w-3.5" /> {t("leads.followUpDone")}</span> : null}
                    </div>
                  </div>

                  {/* lead quality (score) */}
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-quality">
                    <div className="flex items-center justify-between">
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-medium uppercase tracking-wider ${TEMP_BADGE[effTemp(l)]}`} data-testid="detail-temp">{TEMP_ICON[effTemp(l)]}{t(`leads.${effTemp(l)}`)} — {l.lead_score ?? 0}/100</span>
                      {l.lead_temperature_override ? <span className="text-[10px] uppercase tracking-wider text-white/45">{t("leads.manual")}</span> : <span className="text-[10px] uppercase tracking-wider text-white/35">{t("leads.automatic")}</span>}
                    </div>
                    {(l.lead_score_breakdown || []).length ? (
                      <ul className="mt-3 space-y-1" data-testid="detail-quality-why">
                        <li className="text-[11px] uppercase tracking-wider text-[#D6A653]">{t("leads.whyThisLead")}</li>
                        {(l.lead_score_breakdown || []).map((b, i) => (
                          <li key={i} className="flex items-center justify-between text-xs text-white/65"><span>{t(`leads.score_${b.code}`, { defaultValue: b.code.replace(/_/g, " ") })}</span><span className="text-white/80">+{b.points}</span></li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {["hot", "warm", "cold", "auto"].map((tt) => (
                        <button key={tt} onClick={() => setTemperature(l, tt)} data-testid={`detail-temp-${tt}`}
                          className={`rounded-lg border px-2.5 py-1 text-[11px] ${(tt === "auto" && !l.lead_temperature_override) || l.lead_temperature_override === tt ? "border-[#D6A653]/60 bg-[#D6A653]/15 text-[#D6A653]" : "border-white/12 text-white/60 hover:text-white"}`}>
                          {tt === "auto" ? t("leads.useAutomatic") : t(`leads.${tt}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* pipeline status */}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-white/45">{t("leads.pipelineStage")}</span>
                    <Select value={st} onValueChange={(v) => changeStage(l, v)}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid="detail-stage"><SelectValue /></SelectTrigger>
                      <SelectContent className="aria-pop">{STAGES.map((s) => <SelectItem key={s} value={s}>{sLabel(s)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {m ? (
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-meeting">
                      <p className="mb-1 text-xs uppercase tracking-wider text-[#D6A653]">{t("leads.meeting")}</p>
                      <p className="text-white/85">{m.meeting_type_title} — {mLabel(m.status)}</p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-white/55"><Clock className="h-3 w-3" /> {new Date(m.start_utc).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: m.owner_timezone })} ({m.owner_timezone})</p>
                      <button onClick={() => navigate("/meetings")} className="mt-2 text-xs text-[#D6A653] hover:underline" data-testid="detail-view-meeting"><CalendarDays className="mr-1 inline h-3 w-3" /> {t("leads.openInMeetings")}</button>
                    </div>
                  ) : null}

                  {/* activity timeline */}
                  <div>
                    <p className="mb-2 text-xs uppercase tracking-wider text-[#D6A653]">{t("leads.activity")}</p>
                    <ul className="space-y-2">
                      {timeline.map((e, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-white/60" data-testid={`timeline-${i}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-[#D6A653]" />
                          <span className="text-white/80">{evLabel(e.event)}</span>
                          {e.detail ? <span className="text-white/40">· {e.detail}</span> : null}
                          <span className="ml-auto text-white/35">{fmt(e.at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* AI follow-up */}
                  <div id="lead-ai-section" className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="detail-ai-section">
                    <div className="mb-2 grid grid-cols-3 gap-2">
                      <Select value={aiOpts.channel} onValueChange={(v) => setAiOpts((o) => ({ ...o, channel: v }))}><SelectTrigger className="h-8 text-xs" data-testid="ai-channel"><SelectValue /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="email">{t("leads.channel_email")}</SelectItem><SelectItem value="whatsapp">{t("leads.channel_whatsapp")}</SelectItem><SelectItem value="sms">{t("leads.channel_sms")}</SelectItem></SelectContent></Select>
                      <Select value={aiOpts.tone} onValueChange={(v) => setAiOpts((o) => ({ ...o, tone: v }))}><SelectTrigger className="h-8 text-xs" data-testid="ai-tone"><SelectValue /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="professional">{t("leads.tone_professional")}</SelectItem><SelectItem value="warm">{t("leads.tone_warm")}</SelectItem><SelectItem value="short">{t("leads.tone_short")}</SelectItem></SelectContent></Select>
                      <Select value={aiOpts.language} onValueChange={(v) => setAiOpts((o) => ({ ...o, language: v }))}><SelectTrigger className="h-8 text-xs" data-testid="ai-language"><SelectValue /></SelectTrigger><SelectContent className="aria-pop"><SelectItem value="en">EN</SelectItem><SelectItem value="ar">AR</SelectItem><SelectItem value="es">ES</SelectItem></SelectContent></Select>
                    </div>
                    <button onClick={() => genAI(l)} disabled={gen} className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#D6A653] py-2 text-sm font-medium text-[#050607] hover:bg-[#E8B764] disabled:opacity-60" data-testid="detail-ai-generate">{gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> {t("leads.generate")}</>}</button>
                    {draft ? (
                      <div className="mt-2">
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={5} className="w-full rounded-lg border border-white/10 bg-black/30 p-2 text-sm text-white/85 focus:outline-none" data-testid="detail-ai-draft" />
                        <button onClick={() => { navigator.clipboard.writeText(draft); toast.success(t("leads.copied")); }} className="mt-1 flex items-center gap-1 text-xs text-[#D6A653]" data-testid="detail-ai-copy"><Copy className="h-3 w-3" /> {t("leads.copyReview")}</button>
                      </div>
                    ) : null}
                  </div>

                  <button onClick={() => remove(l)} className="flex items-center gap-1.5 text-xs text-red-400/80 hover:text-red-400" data-testid="detail-delete"><Trash2 className="h-3.5 w-3.5" /> {t("leads.deleteLead")}</button>
                  </>
                  )}
                </div>
              </>
            );
          })() : null}
        </DialogContent>
      </Dialog>

      {bookLead ? (
        <BookMeetingDialog
          open={!!bookLead}
          onOpenChange={(v) => { if (!v) { setBookLead(null); load(); } }}
          slug={bookLead.cardSlug}
          initialGuest={{ name: bookLead.name, email: bookLead.email, phone: bookLead.phone }}
        />
      ) : null}

      <LeadFormDialog open={addLeadOpen} onClose={() => setAddLeadOpen(false)} onCreated={() => load()} />
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Phase 1 — LeadTimeline (quick-add + filter chips + merged feed)
// ═════════════════════════════════════════════════════════════════════════
const FILTER_MAP = {
  all: null,
  notes: ["note", "call", "meeting_recap", "email_recap", "event"],
  meetings: ["meeting_booked", "meeting_completed", "meeting_cancelled"],
  scans: ["card_scanned"],
  emails: ["email_recap"],
  ai: ["ai_insight"],
};
const TYPE_META = {
  note: { icon: MessageSquare, label: "Note" },
  call: { icon: Phone, label: "Call" },
  meeting_recap: { icon: CalendarDays, label: "Meeting recap" },
  email_recap: { icon: Mail, label: "Email recap" },
  event: { icon: ListChecks, label: "Event" },
  meeting_booked: { icon: CalendarPlus, label: "Meeting booked" },
  meeting_completed: { icon: Check, label: "Meeting completed" },
  meeting_cancelled: { icon: X, label: "Meeting cancelled" },
  card_scanned: { icon: ScanLine, label: "Card scanned" },
  ai_insight: { icon: Bot, label: "AI insight" },
  status_change: { icon: TrendingUp, label: "Status change" },
};

function LeadTimeline({ leadId, onNoteAdded, user }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("note");
  const [submitting, setSubmitting] = useState(false);
  const [members, setMembers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const load = () => {
    const kinds = FILTER_MAP[filter];
    const qs = kinds ? `?type=${kinds.join(",")}` : "";
    api.get(`/crm/leads/${leadId}/timeline${qs}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  };
  useEffect(load, [leadId, filter]);
  useEffect(() => {
    api.get(`/team/members`).then(({ data }) => setMembers(data.members || [])).catch(() => {});
  }, []);

  const onBodyChange = (e) => {
    const v = e.target.value;
    setBody(v);
    const at = v.lastIndexOf("@");
    if (at !== -1) {
      const after = v.slice(at + 1);
      if (/^[A-Za-z0-9._\-@]*$/.test(after) && after.length <= 40) {
        setMentionQuery(after.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };
  const selectMention = (u) => {
    const at = body.lastIndexOf("@");
    setBody(body.slice(0, at) + `@${u.user_id} `);
    setShowMentions(false);
  };
  const mentionCandidates = useMemo(
    () => (members || []).filter((m) => (m.name || m.email || "").toLowerCase().includes(mentionQuery)).slice(0, 6),
    [members, mentionQuery],
  );

  const submit = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/crm/leads/${leadId}/notes`, { body: body.trim(), interaction_type: kind });
      setBody(""); setKind("note");
      toast.success("Note added");
      load();
      onNoteAdded && onNoteAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setSubmitting(false); }
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/crm/leads/${leadId}/notes/${id}`, { body: editBody });
      setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Edit failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/crm/leads/${leadId}/notes/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const canEditNote = (n) => {
    if (n.actor?.user_id !== user?.id) {
      // Owner/Admin override — approximated client-side by user.role if present.
      return user?.role === "WORKSPACE_OWNER" || user?.role === "WORKSPACE_ADMIN";
    }
    const until = n.payload?.editable_until;
    if (!until) return true;
    return new Date(until) > new Date();
  };

  return (
    <div className="space-y-3">
      {/* Quick-add */}
      <div className="relative rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <textarea
          value={body}
          onChange={onBodyChange}
          onKeyDown={onKey}
          rows={2}
          placeholder="Add a note about this lead — what did you discuss, next steps, context…"
          data-testid="lead-timeline-quickadd-input"
          className="w-full resize-none rounded-md border border-white/12 bg-black/40 p-2 text-xs text-white placeholder:text-white/35 focus:border-[#D6A653]/50 focus:outline-none"
        />
        {showMentions && mentionCandidates.length > 0 && (
          <div className="absolute z-10 mt-1 max-h-40 w-56 overflow-y-auto rounded-lg border border-white/12 bg-[#0A0B0D] shadow-xl">
            {mentionCandidates.map((mm) => (
              <button
                key={mm.user_id}
                onClick={() => selectMention(mm)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-white/85 hover:bg-white/5"
              >
                <User className="h-3 w-3 text-[#D6A653]" />
                <span className="truncate">{mm.name || mm.email}</span>
              </button>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-8 w-[160px] border-white/12 bg-black/40 text-[11px] text-white" data-testid="lead-timeline-quickadd-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0A0B0D] text-white">
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="call">Call</SelectItem>
              <SelectItem value="meeting_recap">Meeting recap</SelectItem>
              <SelectItem value="email_recap">Email recap</SelectItem>
              <SelectItem value="event">Event</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={submit}
            disabled={submitting || !body.trim()}
            data-testid="lead-timeline-quickadd-submit"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#D6A653] px-3 py-1.5 text-xs font-medium text-black disabled:opacity-50"
          >
            <Send className="h-3 w-3" /> Add
            <span className="ml-1 text-[10px] opacity-70">⌘⏎</span>
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {["all", "notes", "meetings", "scans", "emails", "ai"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`lead-timeline-filter-${k}`}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] capitalize ${filter === k ? "border-[#D6A653]/60 bg-[#D6A653]/10 text-[#D6A653]" : "border-white/12 text-white/60 hover:text-white/85"}`}
          >{k}</button>
        ))}
      </div>

      {/* Feed */}
      {items === null ? (
        <div className="flex items-center gap-2 py-4 text-xs text-white/50"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/12 p-6 text-center text-xs text-white/55">
          <p>No events yet.</p>
          <p className="mt-1 text-[10px] text-white/40">↑ Add the first note above.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((ev) => {
            const M = TYPE_META[ev.type] || TYPE_META.note;
            const Icon = M.icon;
            const editable = ev.type === "note" || ev.type === "call" || ev.type === "meeting_recap" || ev.type === "email_recap" || ev.type === "event";
            return (
              <li key={ev.id} data-testid={`lead-timeline-item-${ev.type === "ai_insight" ? "ai_insight" : (ev.type.startsWith("meeting") ? "meeting" : ev.type === "card_scanned" ? "scan" : ev.type === "email_recap" ? "email" : "note")}`}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#D6A653]/10 text-[#D6A653]">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-white/85">
                        <b>{M.label}</b>
                        <span className="ml-2 text-white/45">{ev.actor?.name}</span>
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-white/40" title={ev.timestamp}>{relTime(ev.timestamp)}</span>
                        {editable && canEditNote(ev) && (
                          <div className="relative" data-testid="lead-timeline-item-menu">
                            <details className="[&_summary::-webkit-details-marker]:hidden">
                              <summary className="cursor-pointer list-none rounded p-1 text-white/50 hover:bg-white/5"><MoreHorizontal className="h-3 w-3" /></summary>
                              <div className="absolute right-0 z-10 mt-1 w-32 rounded-lg border border-white/10 bg-[#0A0B0D] shadow-xl">
                                <button onClick={() => { setEditingId(ev.id); setEditBody(ev.payload?.body || ""); }}
                                        data-testid="lead-timeline-item-edit"
                                        className="block w-full px-3 py-1.5 text-left text-xs text-white/80 hover:bg-white/5">Edit</button>
                                <button onClick={() => remove(ev.id)}
                                        data-testid="lead-timeline-item-delete"
                                        className="block w-full px-3 py-1.5 text-left text-xs text-red-300 hover:bg-red-500/10">Delete</button>
                              </div>
                            </details>
                          </div>
                        )}
                      </div>
                    </div>
                    {editingId === ev.id ? (
                      <div className="mt-2 space-y-2">
                        <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2}
                                  className="w-full resize-none rounded-md border border-white/12 bg-black/40 p-2 text-xs text-white" />
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(ev.id)} className="rounded-md bg-[#D6A653] px-2 py-1 text-[11px] text-black">Save</button>
                          <button onClick={() => setEditingId(null)} className="rounded-md border border-white/12 px-2 py-1 text-[11px] text-white/70">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1 whitespace-pre-wrap break-words text-xs text-white/75">
                        {ev.payload?.body || (
                          ev.type === "card_scanned" ? `via ${ev.payload?.source || "scanner"}` :
                          ev.type.startsWith("meeting") ? `${ev.payload?.meeting_type || "Meeting"}${ev.payload?.start_utc ? ` · ${new Date(ev.payload.start_utc).toLocaleString()}` : ""}` :
                          ev.type === "status_change" ? `Status → ${ev.payload?.status}` : ""
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
